import React, { useContext, useMemo, useState } from "react";
import LoadingCircle from "../../../components/LoadingCircle/LoadingCircle";
import { http } from "../../../utils/httpCommon";
import MonthView from "./MonthView";
import moment from "moment";
import ListView from "./ListView";
import { useDisclosure } from "@mantine/hooks";
import { Drawer } from "@mantine/core";
import DayView from "./DayView";
import CalendarForm from "./Forms/CalendarForm";
import { SelectedLocationContext } from "../../../Context/LocationProvider";
import { useAuthUser } from "react-auth-kit";
import { DebouncedInput } from "../../../components/ui/DebouncedInput";
import { useQuery } from "@tanstack/react-query";
import { encodedURIUrl } from "../../../utils/encodeUrlParams";
import { toast } from "react-hot-toast";

/** Matches backend MAX_CALENDAR_LIMIT — loads visible window only, not full history */
export const CALENDAR_VISIBLE_RANGE_LIMIT = 5000;

function getVisibleDateRange(viewState, selectedDate, currMonth, listStartDate, listEndDate) {
  if (viewState === "Day") {
    const day = selectedDate.format("YYYY-MM-DD");
    return { from: day, to: day };
  }
  if (viewState === "Month") {
    const month = moment(currMonth);
    return {
      from: month.clone().startOf("month").format("YYYY-MM-DD"),
      to: month.clone().endOf("month").format("YYYY-MM-DD"),
    };
  }
  return {
    from: moment(listStartDate).format("YYYY-MM-DD"),
    to: moment(listEndDate).format("YYYY-MM-DD"),
  };
}

function flattenBookingsToAppointments(bookings = []) {
  const flatedData = bookings.flatMap((item) => {
    const days = (item.days || []).map((day) => ({
      ...day,
      bookingId: item?._id,
      dayId: day?._id,
      location: item?.location,
      isConfirmed: item?.isConfirmed,
      isDeleted: item?.isDeleted,
      facility: item?.facility,
      isReceivable: Boolean(item?.receivable),
      user: item?.user,
      createdAt: item?.createdAt,
      updatedAt: item?.updatedAt,
      invoices: item?.invoice ?? [],
      totalWithDiscount: item?.totalWithDiscount ?? 0,
    }));
    return days;
  });

  return flatedData.map((a) => ({
    ...a,
    id: a?._id,
    startDate: new Date(a.startTime * 1000),
    endDate: new Date(a.endTime * 1000),
    roomId: a?.facility?._id,
    location: a?.facility?.name,
  }));
}

const Calender = () => {
  const { selectedLocation } = useContext(SelectedLocationContext);

  const {
    data: facilities,
    isLoading: loadingFacilities,
  } = useQuery({
    queryKey: ["calendar-facilities", selectedLocation],
    queryFn: async ({ signal }) => {
      const res = await http.post(
        "/facility/all",
        {
          page: 1,
          limit: 1000,
          location: selectedLocation,
        },
        { signal }
      );
      return res.data.data;
    },
    enabled: Boolean(selectedLocation),
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["location-payment-methods", selectedLocation],
    queryFn: async ({ signal }) => {
      const res = await http.get(`/location/${selectedLocation}/payment-methods`, { signal });
      return res.data.data;
    },
    enabled: Boolean(selectedLocation),
  });
  const auth = useAuthUser();

  const [selectedDate, setSelectedDate] = useState(moment());
  const [currMonth, setCurrMonth] = useState(new Date());
  const [search, setSearch] = useState("");
  const initialStart = new Date(new Date().setDate(new Date().getDate() - 7));
  const [listStartDate, setListStartDate] = useState(initialStart);
  const [listEndDate, setListEndDate] = useState(new Date());
  const [viewState, setViewState] = useState("Day");

  const dateRange = useMemo(
    () => getVisibleDateRange(viewState, selectedDate, currMonth, listStartDate, listEndDate),
    [viewState, selectedDate, currMonth, listStartDate, listEndDate]
  );

  const bookingQueryParams = useMemo(
    () => ({
      baseForm: "true",
      page: 1,
      limit: CALENDAR_VISIBLE_RANGE_LIMIT,
      location: selectedLocation,
      from: dateRange.from,
      to: dateRange.to,
      ...(search ? { search } : {}),
    }),
    [selectedLocation, dateRange.from, dateRange.to, search]
  );

  const {
    data: appointmentsData = [],
    isLoading: appointmentsDataLoading,
    refetch: refetchAppointments,
  } = useQuery({
    queryKey: ["calendar-bookings", bookingQueryParams],
    queryFn: async ({ signal }) => {
      const url = encodedURIUrl("/bookings", bookingQueryParams);
      const res = await http.get(url, { signal });
      const count = res?.data?.count ?? 0;
      if (count > CALENDAR_VISIBLE_RANGE_LIMIT) {
        toast(
          `Showing first ${CALENDAR_VISIBLE_RANGE_LIMIT} of ${count} bookings. Narrow the date range or search.`,
          { icon: "⚠️", duration: 5000 }
        );
      }
      return flattenBookingsToAppointments(res?.data?.data || []);
    },
    enabled: Boolean(selectedLocation),
  });

  const fetchAppointmentsData = React.useCallback(
    () => refetchAppointments(),
    [refetchAppointments]
  );
  const fetchAppointmentsDataAfterDelete = fetchAppointmentsData;

  const [opened, { open, close }] = useDisclosure(false);

  const [selectedTimeSlotData, setSelectedTimeSlotData] = useState({
    time: "",
    date: "",
    facility: {},
  });

  return (
    <div className="relative ">
      {!appointmentsDataLoading && !loadingFacilities ? (
        <>
          <Drawer
            opened={opened}
            onClose={() => {
              close();
              fetchAppointmentsDataAfterDelete();
            }}
            position="right"
            size={"lg"}
            overlayProps={{ opacity: 0.6, blur: 5 }}
          >
            <CalendarForm
              selectedTimeSlotData={selectedTimeSlotData}
              open={open}
              close={close}
              facilities={facilities}
              paymentMethods={paymentMethods}
              fetchAppointmentsData={fetchAppointmentsData}
              fetchAppointmentsDataAfterDelete={fetchAppointmentsDataAfterDelete}
            />
          </Drawer>
          <div className="flex justify-between relative">
            <div className="flex ">
              <button
                className={`${
                  viewState === "Day" ? "bg-[#EFEFEF]" : "bg-[#F8F9FA]"
                }  transition-all duration-100 text-black rounded-tl-[10px] rounded-bl-[10px]  border-[1px] border-[#E4E4E4] font-bold px-[15px] py-[10px] text-[18px] h-fit`}
                onClick={() => setViewState("Day")}
              >
                Day
              </button>
              <button
                className={`${
                  viewState === "Month" ? "bg-[#EFEFEF]" : "bg-[#F8F9FA]"
                } transition-all duration-100 text-black border-t-[1px] border-b-[1px] border-r-[1px]  border-[#E4E4E4] font-bold px-[15px] py-[10px] text-[18px] h-fit`}
                onClick={() => setViewState("Month")}
              >
                Month
              </button>
              <button
                className={`${
                  viewState === "List" ? "bg-[#EFEFEF]" : "bg-[#F8F9FA]"
                } transition-all duration-100 text-black rounded-tr-[10px] rounded-br-[10px] border-t-[1px]   border-b-[1px]  border-r-[1px] border-[#E4E4E4] font-bold px-[15px] py-[10px] text-[18px] h-fit`}
                onClick={() => setViewState("List")}
              >
                List
              </button>
            </div>
            <div className="flex items-center gap-3 mr-[5%]">
              <DebouncedInput
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or phone"
                className="h-[40px] w-[260px] px-3 border border-[#E4E4E4] rounded-[8px] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#34B2C0]"
              />
              {auth().role !== "Land Lord" && (
                <button
                  onClick={open}
                  className="w-[50px] h-[50px] bg-[#34B2C0] rounded-full flex justify-center items-center text-[#EFEFEF] text-[40px] shadow-2xl shadow-[#DEDEDE] hover:opacity-90 transition-all duration-100 "
                >
                  +
                </button>
              )}
            </div>
          </div>
          {facilities?.count === 0 ? (
            <div className="flex justify-center items-center mt-10 text-[20px] font-bold ">
              No Facilities in this location yet
            </div>
          ) : (
            <>
              {viewState === "Day" ? (
                <DayView
                  open={open}
                  close={close}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  facilities={facilities}
                  paymentMethods={paymentMethods}
                  appointmentsData={appointmentsData}
                  selectedTimeSlotData={selectedTimeSlotData}
                  setSelectedTimeSlotData={setSelectedTimeSlotData}
                  fetchAppointmentsData={fetchAppointmentsData}
                  fetchAppointmentsDataAfterDelete={fetchAppointmentsDataAfterDelete}
                />
              ) : viewState === "Month" ? (
                <MonthView
                  currentDate={currMonth}
                  setCurrentDate={setCurrMonth}
                  facilities={facilities}
                  paymentMethods={paymentMethods}
                  appointmentsData={appointmentsData}
                  fetchAppointmentsData={fetchAppointmentsData}
                  fetchAppointmentsDataAfterDelete={fetchAppointmentsDataAfterDelete}
                />
              ) : (
                <ListView
                  appointmentsData={appointmentsData}
                  startDateValue={listStartDate}
                  endDateValue={listEndDate}
                  setStartDateValue={setListStartDate}
                  setEndDateValue={setListEndDate}
                />
              )}
            </>
          )}
        </>
      ) : (
        <LoadingCircle />
      )}
    </div>
  );
};

export default Calender;
